import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { HttpStatusCodeEnum } from '@enum/http-status-code.enum';
import { catchError, EMPTY, throwError } from 'rxjs';

import { TokenService } from '@services/helper/token.service';

/**
 * HTTP interceptor that:
 * 1. Attaches the JWT Bearer token to outgoing requests.
 * 2. Handles every 401 according to these rules:
 *    - `/login` 401 → wrong credentials; let the login form surface it.
 *    - `/refresh` 401 → the refresh token itself is dead; log out.
 *    - Refresh enabled ("Remember Me") → queue the request behind one silent
 *      token refresh, then retry it.
 *    - Refresh disabled (no "Remember Me") → any 401 ends the session and
 *      redirects to login.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const token = tokenService.getToken();

  /* Attach Bearer token if available */
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      /* Anything that isn't a 401 bubbles up untouched. */
      if (error.status !== HttpStatusCodeEnum.Unauthorized) {
        return throwError(() => error);
      }

      /* A 401 on /login is a failed sign-in, not an expired session. */
      if (req.url.includes('/login')) {
        return throwError(() => error);
      }

      /* A 401 on /refresh means refreshing is no longer possible — log out.
       * Surface the error too so TokenService's refresh pipeline unwinds. */
      if (req.url.includes('/refresh')) {
        tokenService.logout();
        return throwError(() => error);
      }

      /* Token expired but "Remember Me" is on — refresh silently and retry. */
      if (tokenService.getAllowRefreshTokenStatus()) {
        return tokenService.addToRefreshQueue(req, next);
      }

      /* "Remember Me" off — any 401 ends the session immediately. */
      tokenService.logout();
      return EMPTY;
    }),
  );
};
